import React, { useState, useEffect } from "react";
import { ShieldAlert, Phone, Mail, CheckCircle2, AlertCircle, RefreshCw, Info, HardDrive } from "lucide-react";
import { fetchDRSummary, DisasterRecoverySummaryResponse } from "./settingsApi";

export const DisasterRecoveryPage: React.FC = () => {
  const [telemetry, setTelemetry] = useState<DisasterRecoverySummaryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadDRSummary(true);
  }, []);

  const loadDRSummary = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError("");

    try {
      const data = await fetchDRSummary();
      setTelemetry(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load Disaster Recovery telemetry.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-rose-400" />
        <span>Evaluating Disaster Recovery readiness & backup evidence...</span>
      </div>
    );
  }

  const status = telemetry?.dr_status;
  const backup = telemetry?.backup_summary;
  const restore = telemetry?.restore_summary;
  const checklist = telemetry?.checklist || [];
  const contactsObj = telemetry?.emergency_contacts;
  const contacts = contactsObj?.contacts || [];

  const verifiedCount = checklist.filter((c) => c.verified).length;
  const totalSteps = checklist.length;

  const statusColor =
    status?.code === "READY"
      ? "bg-emerald-950/80 border-emerald-800 text-emerald-300"
      : status?.code === "WARNING"
      ? "bg-amber-950/80 border-amber-800 text-amber-300"
      : "bg-rose-950/80 border-rose-800 text-rose-300";

  return (
    <div className="space-y-4 text-xs font-sans">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header & DR Readiness Status */}
      <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 border rounded-xl gap-3 ${statusColor}`}>
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 shrink-0 mt-0.5" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold">Disaster Recovery & Readiness Overview</h2>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold uppercase bg-black/40 border border-current">
                DR Status: {status?.code || "UNAVAILABLE"}
              </span>
            </div>
            <p className="text-[11px] opacity-90 mt-0.5">
              {status?.reasons?.join(" ") || "Evaluating system backup evidence and offsite recovery readiness."}
            </p>
          </div>
        </div>

        <button
          onClick={() => loadDRSummary(false)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 disabled:opacity-50 text-slate-100 rounded-lg transition-colors text-[11px] font-sans border border-slate-700/50 shrink-0 ml-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-rose-400" : ""}`} />
          <span>{refreshing ? "Refreshing..." : "Refresh Status"}</span>
        </button>
      </div>

      {/* DR Telemetry Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
          <p className="text-sm font-bold font-mono text-emerald-400 truncate">
            {backup?.latest_successful?.started_at ? new Date(backup.latest_successful.started_at).toLocaleDateString() : "None"}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Latest Local Backup</p>
        </div>

        <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
          <p className="text-sm font-bold font-mono text-purple-400 truncate">
            {backup?.latest_r2_offsite?.completed_at ? new Date(backup.latest_r2_offsite.completed_at).toLocaleDateString() : "None"}
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Latest R2 Offsite Snapshot</p>
        </div>

        <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
          <p className="text-sm font-bold font-mono text-indigo-400">
            {restore?.dr_drills_count || 0} Drills
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">Executed DR Restore Drills</p>
        </div>

        <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl text-center">
          <p className="text-sm font-bold font-mono text-amber-400">
            Unavailable
          </p>
          <p className="text-[10px] text-slate-500 mt-0.5">RTO / RPO Target</p>
        </div>
      </div>

      {/* Verified Incident Recovery Runbook */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase font-sans">
            Evidence-Backed Incident Recovery Runbook ({verifiedCount}/{totalSteps} Controls Verified)
          </h3>
          <span className="text-[10px] text-slate-500 font-mono">Backend Automated Audit</span>
        </div>

        <div className="space-y-2">
          {checklist.map((step, idx) => (
            <div
              key={step.id}
              className={`p-3 border rounded-xl transition-all ${
                step.verified
                  ? "bg-emerald-950/20 border-emerald-800/50"
                  : "bg-slate-950 border-slate-800"
              }`}
            >
              <div className="flex items-start gap-2.5">
                <span className="text-[10px] font-mono text-slate-600 mt-0.5">{String(idx + 1).padStart(2, "0")}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-[11px] font-bold ${step.verified ? "text-emerald-300" : "text-slate-200"}`}>
                      {step.step}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold shrink-0 ${
                        step.verified
                          ? "bg-emerald-950 text-emerald-400 border border-emerald-800"
                          : "bg-slate-800 text-slate-400 border border-slate-700"
                      }`}
                    >
                      {step.verified ? "Verified System Evidence" : "Not Verified (Manual Check Required)"}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">{step.description}</p>
                  <p className="text-[10px] font-mono text-slate-400 mt-1 bg-slate-900/90 px-2 py-1 rounded border border-slate-800/80">
                    Evidence: {step.evidence}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Emergency Contacts */}
      <div className="p-4 bg-slate-900/80 border border-slate-800 rounded-xl space-y-3">
        <h3 className="text-[11px] font-bold font-mono text-slate-400 uppercase font-sans">
          Registered Emergency Incident Contacts
        </h3>

        {contacts.length === 0 ? (
          <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl text-center text-slate-500 text-[11px]">
            <p className="font-bold text-slate-400">{contactsObj?.note || "Emergency contacts not configured."}</p>
            <p className="text-[10px] text-slate-600 mt-0.5">Registered superuser administrator accounts will automatically appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {contacts.map((c) => (
              <div key={c.email} className="flex items-start gap-3 p-3 bg-slate-950 border border-slate-800 rounded-xl font-sans">
                <ShieldAlert className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="font-bold text-slate-100 text-[11px]">{c.name}</p>
                  <p className="text-[10px] text-slate-500 font-mono">{c.role}</p>
                  <div className="flex items-center gap-3 mt-1.5 font-mono">
                    <a href={`mailto:${c.email}`} className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-200 transition-colors truncate">
                      <Mail className="w-3 h-3 shrink-0" />{c.email}
                    </a>
                    {c.phone && c.phone !== "Not Configured" && (
                      <a href={`tel:${c.phone}`} className="flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-200 transition-colors">
                        <Phone className="w-3 h-3 shrink-0" />{c.phone}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Limitations & Target Metrics Banner */}
      <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-start gap-2 text-[11px] text-slate-400">
        <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-slate-300 block mb-0.5 font-sans">Disaster Recovery Metrics Scope</span>
          <p className="text-[10px] text-slate-500 font-sans">
            Recovery Time Objective (RTO Target) and Recovery Point Objective (RPO Target) values are <strong className="text-slate-400 font-mono">Unavailable</strong> as static metrics because true RTO/RPO depend on database dump sizes and automated backup cron execution frequency.
          </p>
        </div>
      </div>
    </div>
  );
};
